import { createContext, useReducer, useEffect, useContext, useRef } from "react";
import { assertNever } from "@opencast/appkit";

import {
  isDisplayCaptureSupported,
  isUserCaptureSupported,
  usePresentContext,
  isWebGL2Available,
  isCanvas2DFilterAvailable,
  onSafari,
} from "./util";

import { removeSessionToken } from "./mediasite";
import { useSettings } from "./settings";
import { startUserCapture } from "./capturer";
import ToastContext from "./context/toast-context";
import type { ToastContextType, ToastType } from "./context/toast-context";
import { useTranslation } from "react-i18next";
import {
  loadAudioNoiseSuppressionMode,
} from "./audio/noise-suppression/prefs";
import type { AudioNoiseSuppressionMode } from "./audio/noise-suppression/prefs";
import { isNoiseSuppressionSupported } from "./audio/noise-suppression/detectNoiseSuppressionCapabilities";

export type AudioSource = "microphone" | "none";
export type VideoSource = "both" | "display" | "user" | "none";
export type UploadState = "not_uploaded" | "uploading" | "uploaded" | "error";
export type BlurMode = "none" | "subtle" | "strong";

const TOAST_SUPPRESSION_ON_RECORDING_TRANSITION_MS = 2000;

export type Recording = {
  deviceType: "desktop" | "video";
  media: Blob;
  url: string;
  mimeType: string;
  dimensions: [number, number] | null;
  downloaded?: boolean;
  fileName: string;
  localMedia: Blob;
};

/** Our global state */
export type StudioState = {
  hasWebcam: boolean;
  mediaDevices: MediaDeviceInfo[];

  audioAllowed: null | boolean;
  audioStream: null | MediaStream;
  // Device ID of the raw microphone track, captured before noise suppression
  // wraps it in a device-less synthetic track (destination node tracks report
  // no deviceId via getSettings()).
  audioDeviceId: null | string;
  audioUnexpectedEnd: boolean;
  audioSupported: boolean;
  audioNoiseSuppressionMode: AudioNoiseSuppressionMode;
  audioNoiseSuppressionAvailable: boolean;

  displayAllowed: null | boolean;
  displayStream: null | MediaStream;
  displayUnexpectedEnd: boolean;
  displaySupported: boolean;

  userAllowed: null | boolean;
  userStream: null | MediaStream;
  processedUserStream: null | MediaStream;
  userUnexpectedEnd: boolean;
  userSupported: boolean;

  videoMirror: boolean;
  videoBlur: BlurMode;
  videoBlurAvailable: boolean;
  videoBlurLoadingCount: number;
  audioNoiseSuppressionLoadingCount: number;

  videoChoice: VideoSource;
  audioChoice: AudioSource;

  isRecording: boolean;
  isUploading: boolean;
  prematureRecordingEnd: boolean;
  recordings: Recording[];

  title: string;
  presenter: string;

  start: null | number;
  end: null | number;

  recordingStartTime: null | Date;
  recordingEndTime: null | Date;

  upload: {
    error: null | string;
    state: UploadState;
    secondsLeft: null | number;
    currentProgress: number;
  };
};

const initialState = (hasWebcam: boolean): StudioState => ({
  hasWebcam,
  mediaDevices: [],

  audioAllowed: null,
  audioStream: null,
  audioDeviceId: null,
  audioUnexpectedEnd: false,
  audioSupported: isUserCaptureSupported(),
  audioNoiseSuppressionMode: loadAudioNoiseSuppressionMode(),
  audioNoiseSuppressionAvailable: isNoiseSuppressionSupported(),

  displayAllowed: null,
  displayStream: null,
  displayUnexpectedEnd: false,
  displaySupported: isDisplayCaptureSupported(),

  userAllowed: null,
  userStream: null,
  processedUserStream: null,
  userUnexpectedEnd: false,
  userSupported: isUserCaptureSupported(),

  videoMirror: false,
  videoBlur: "none",
  // Safari has known issues with WebGL-based blur; disable it entirely.
  videoBlurAvailable: !onSafari() && (isWebGL2Available() || isCanvas2DFilterAvailable()),
  videoBlurLoadingCount: 0,
  audioNoiseSuppressionLoadingCount: 0,

  videoChoice: "none",
  audioChoice: "none",

  isRecording: false,
  isUploading: false,
  prematureRecordingEnd: false,
  recordings: [],

  title: "",
  presenter: "",

  start: null,
  end: null,

  recordingStartTime: null,
  recordingEndTime: null,

  upload: {
    error: null,
    state: "not_uploaded",
    secondsLeft: null,
    currentProgress: 0,
  },
});

/** Every possible action that can be passed to the reducer. */
type ReducerAction =
  | { type: "UPDATE_MEDIA_DEVICES"; devices: MediaDeviceInfo[] }
  | { type: "CHOOSE_AUDIO"; choice: AudioSource }
  | { type: "CHOOSE_VIDEO"; choice: VideoSource }
  | { type: "SHARE_AUDIO"; stream: MediaStream; deviceId?: string | null }
  | { type: "BLOCK_AUDIO" }
  | { type: "UNSHARE_AUDIO" }
  | { type: "AUDIO_UNEXPECTED_END" }
  | { type: "SET_AUDIO_NOISE_SUPPRESSION_MODE"; mode: AudioNoiseSuppressionMode }
  | { type: "SET_AUDIO_NOISE_SUPPRESSION_AVAILABLE"; available: boolean }
  | { type: "SHARE_DISPLAY"; stream: MediaStream }
  | { type: "BLOCK_DISPLAY" }
  | { type: "UNSHARE_DISPLAY" }
  | { type: "DISPLAY_UNEXPECTED_END" }
  | { type: "SHARE_USER"; stream: MediaStream }
  | { type: "SHARE_PROCESSED_USER"; stream: MediaStream }
  | { type: "BLOCK_USER" }
  | { type: "UNSHARE_USER" }
  | { type: "UNSHARE_PROCESSED_USER" }
  | { type: "USER_UNEXPECTED_END" }
  | { type: "SET_VIDEO_MIRROR"; enabled: boolean }
  | { type: "SET_VIDEO_BLUR"; mode: BlurMode }
  | { type: "SET_VIDEO_BLUR_AVAILABLE"; available: boolean }
  | { type: "BEGIN_VIDEO_BLUR_LOADING" }
  | { type: "END_VIDEO_BLUR_LOADING" }
  | { type: "BEGIN_AUDIO_NOISE_SUPPRESSION_LOADING" }
  | { type: "END_AUDIO_NOISE_SUPPRESSION_LOADING" }
  | { type: "START_RECORDING" }
  | { type: "STOP_RECORDING" }
  | { type: "STOP_RECORDING_PREMATURELY" }
  | { type: "CLEAR_RECORDINGS" }
  | { type: "ADD_RECORDING"; recording: Recording }
  | { type: "UPLOAD_ERROR"; msg: string }
  | { type: "UPLOAD_REQUEST" }
  | { type: "UPLOAD_SUCCESS" }
  | { type: "UPLOAD_PROGRESS_UPDATE"; secondsLeft: number | null; currentProgress: number }
  | { type: "MARK_DOWNLOADED"; index: number }
  | { type: "UPDATE_TITLE"; value: string }
  | { type: "UPDATE_PRESENTER"; value: string }
  | { type: "UPDATE_START"; time: number | null }
  | { type: "UPDATE_END"; time: number | null }
  | { type: "RESET" }
  | { type: "FORCE_STATE_REFRESH" };


const reducer = (state: StudioState, action: ReducerAction): StudioState => {
  switch (action.type) {
    case "UPDATE_MEDIA_DEVICES": return { ...state, mediaDevices: action.devices };
    case "CHOOSE_AUDIO": return { ...state, audioChoice: action.choice };
    case "CHOOSE_VIDEO": return { ...state, videoChoice: action.choice };

    case "SHARE_AUDIO": return {
      ...state,
      audioStream: action.stream,
      audioDeviceId: action.deviceId ?? state.audioDeviceId,
      audioAllowed: true,
      audioUnexpectedEnd: false,
    };
    case "BLOCK_AUDIO":
      return { ...state, audioStream: null, audioAllowed: false, audioUnexpectedEnd: false };
    case "UNSHARE_AUDIO": return { ...state, audioStream: null, audioUnexpectedEnd: false };
    case "AUDIO_UNEXPECTED_END": return { ...state, audioStream: null, audioUnexpectedEnd: true };
    case "SET_AUDIO_NOISE_SUPPRESSION_MODE":
      return { ...state, audioNoiseSuppressionMode: action.mode };
    case "SET_AUDIO_NOISE_SUPPRESSION_AVAILABLE":
      return {
        ...state,
        audioNoiseSuppressionAvailable: action.available,
        audioNoiseSuppressionMode: action.available ? state.audioNoiseSuppressionMode : "none",
      };

    case "SHARE_DISPLAY": return {
      ...state,
      displayStream: action.stream,
      displayAllowed: true,
      displayUnexpectedEnd: false,
    };
    case "BLOCK_DISPLAY":
      return { ...state, displayStream: null, displayAllowed: false, displayUnexpectedEnd: false };
    case "UNSHARE_DISPLAY":
      return { ...state, displayStream: null, displayAllowed: null, displayUnexpectedEnd: false };
    case "DISPLAY_UNEXPECTED_END":
      return { ...state, displayStream: null, displayUnexpectedEnd: true };

    case "SHARE_USER":
      return { ...state, userStream: action.stream, userAllowed: true, userUnexpectedEnd: false };
    case "SHARE_PROCESSED_USER":
      return { ...state, processedUserStream: action.stream };
    case "BLOCK_USER":
      return {
        ...state,
        userStream: null,
        processedUserStream: null,
        userAllowed: false,
        userUnexpectedEnd: false,
      };
    case "UNSHARE_USER":
      return {
        ...state,
        userStream: null,
        processedUserStream: null,
        userAllowed: null,
        userUnexpectedEnd: false,
      };
    case "UNSHARE_PROCESSED_USER":
      return { ...state, processedUserStream: null };
    case "USER_UNEXPECTED_END":
      return {
        ...state,
        userStream: null,
        processedUserStream: null,
        userUnexpectedEnd: true,
      };
    case "SET_VIDEO_MIRROR":
      return { ...state, videoMirror: action.enabled };
    case "SET_VIDEO_BLUR":
      return { ...state, videoBlur: action.mode };
    case "SET_VIDEO_BLUR_AVAILABLE":
      return { ...state, videoBlurAvailable: action.available };
    case "BEGIN_VIDEO_BLUR_LOADING":
      return { ...state, videoBlurLoadingCount: state.videoBlurLoadingCount + 1 };
    case "END_VIDEO_BLUR_LOADING":
      return { ...state, videoBlurLoadingCount: Math.max(0, state.videoBlurLoadingCount - 1) };
    case "BEGIN_AUDIO_NOISE_SUPPRESSION_LOADING":
      return {
        ...state,
        audioNoiseSuppressionLoadingCount: state.audioNoiseSuppressionLoadingCount + 1,
      };
    case "END_AUDIO_NOISE_SUPPRESSION_LOADING":
      return {
        ...state,
        audioNoiseSuppressionLoadingCount: Math.max(0, state.audioNoiseSuppressionLoadingCount - 1),
      };

    case "START_RECORDING": return { ...state, isRecording: true, recordingStartTime: new Date() };
    case "STOP_RECORDING": return { ...state, isRecording: false, recordingEndTime: new Date() };
    case "STOP_RECORDING_PREMATURELY":
      return {
        ...state,
        isRecording: false,
        prematureRecordingEnd: true,
        recordingEndTime: new Date(),
      };
    case "CLEAR_RECORDINGS":
      return { ...state, recordings: [], prematureRecordingEnd: false };
    case "ADD_RECORDING":
      // We remove all recordings with the same device type as the new one. This
      // *should* in theory never happen as all recordings are cleared before
      // new ones can be added. However, in rare case, this might not be true
      // and the user ends up with strange recordings. Just to be sure, we
      // remove old recordings here.
      return {
        ...state,
        recordings: [
          ...state.recordings.filter(r => r.deviceType !== action.recording.deviceType),
          action.recording,
        ],
      };

    case "UPLOAD_ERROR":
      return { ...state, upload: { ...state.upload, error: action.msg, state: "error" } };
    case "UPLOAD_REQUEST":
      return { ...state, isUploading: true, isRecording: true,
        upload: { ...state.upload, error: null, state: "uploading" } };
    case "UPLOAD_SUCCESS":
      removeSessionToken();
      return { ...state, upload: { ...state.upload, error: null, state: "uploaded" } };
    case "UPLOAD_PROGRESS_UPDATE":
      return { ...state, upload: {
        ...state.upload,
        secondsLeft: action.secondsLeft,
        currentProgress: action.currentProgress,
      } };

    case "MARK_DOWNLOADED": return {
      ...state,
      recordings: state.recordings.map((recording, index) => (
        index === action.index ? { ...recording, downloaded: true } : recording
      )),
    };
    case "UPDATE_TITLE": return { ...state, title: action.value };
    case "UPDATE_PRESENTER": return { ...state, presenter: action.value };
    case "UPDATE_START": return { ...state, start: action.time };
    case "UPDATE_END": return { ...state, end: action.time };

    case "RESET": return initialState(state.hasWebcam);
    case "FORCE_STATE_REFRESH":
      // Return a shallow-cloned state object to force context consumers
      // to re-evaluate derived values (e.g., audio track enabled flags)
      return { ...state };

    default: assertNever(action);
  }
};

export type Dispatcher = (action: ReducerAction) => void;

const stateContext = createContext<StudioState | null>(null);
const dispatchContext = createContext<Dispatcher | null>(null);

type ProviderProps = React.PropsWithChildren<{
  hasWebcam: boolean;
}>;

const preferredCameraAspectRatio = (): number | undefined => {
  const pref = window.localStorage.getItem("MediasiteWebRecorderCameraAspectRatio") ?? "auto";
  switch (pref) {
    case "4:3":
      return 4 / 3;
    case "16:9":
      return 16 / 9;
    case "9:16":
      return 9 / 16;
    default:
      return undefined;
  }
};

export const Provider: React.FC<ProviderProps> = ({ hasWebcam, children }) => {
  const [state, dispatch] = useReducer(reducer, initialState(hasWebcam));
  const toastCtx: ToastContextType = useContext(ToastContext);
  const settings = useSettings();
  const stateRef = useRef<StudioState | null>(null);

  // Keep a ref to the latest state so async devicechange handler can read it.
  useEffect(() => { stateRef.current = state; }, [state]);
  const { t } = useTranslation();
  const prevDevicesRef = useRef<MediaDeviceInfo[] | null>(null);
  const prevVideoChoiceRef = useRef<VideoSource>(state.videoChoice);
  const prevAudioNoiseSuppressionAvailableRef = useRef<boolean>(state.audioNoiseSuppressionAvailable);
  const suppressToastNotificationsUntilRef = useRef<number>(0);

  const suppressToastNotificationsFor = (durationMs: number) => {
    const now = Date.now();
    suppressToastNotificationsUntilRef.current = Math.max(
      suppressToastNotificationsUntilRef.current,
      now + durationMs,
    );
  };

  const shouldSuppressToastNotifications = () => {
    return Date.now() < suppressToastNotificationsUntilRef.current;
  };

  // Some platforms (notably iOS Safari) surface already-attached devices a bit
  // later when entering recording. Suppress toasts briefly on transition so
  // those late initial enumerations do not look like real hot-plug events.
  useEffect(() => {
    if (prevVideoChoiceRef.current === "none" && state.videoChoice !== "none") {
      suppressToastNotificationsFor(TOAST_SUPPRESSION_ON_RECORDING_TRANSITION_MS);
    }
    prevVideoChoiceRef.current = state.videoChoice;
  }, [state.videoChoice]);

  useEffect(() => {
    const wasAvailable = prevAudioNoiseSuppressionAvailableRef.current;
    prevAudioNoiseSuppressionAvailableRef.current = state.audioNoiseSuppressionAvailable;

    if (wasAvailable && !state.audioNoiseSuppressionAvailable) {
      try {
        if (toastCtx && typeof toastCtx.addItems === "function") {
          toastCtx.addItems([{
            id: Date.now() + Math.floor(Math.random() * 1000),
            status: "warning",
            icon: "",
            link: "",
            title: t("toast-audio-noise-isolation-disabled-title"),
            message: t("toast-audio-noise-isolation-disabled-body"),
            linkText: "",
          }]);
        }
      } catch {
        // best-effort
      }
    }
  }, [state.audioNoiseSuppressionAvailable, t, toastCtx]);

  // Query available media devices on mount and update when the set of
  // devices changes (e.g. USB camera plugged/unplugged). We attach a
  // `devicechange` listener to `navigator.mediaDevices` which fires in most
  // modern browsers when the available devices change.
  useEffect(() => {
    const updateAndNotify = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        dispatch({ type: "UPDATE_MEDIA_DEVICES", devices });

        const prev = prevDevicesRef.current;

        // If this is the initial enumeration on app load, don't show toasts
        // for devices that already exist. Just store the current set and
        // return — subsequent `devicechange` events will trigger notifications.
        if (prev == null) {
          prevDevicesRef.current = devices;
          return;
        }

        const prevIds = new Set(prev.map(d => d.deviceId));
        const newDevices = devices.filter(d => d.kind === "videoinput" && !prevIds.has(d.deviceId));

        // Detect removed devices (present previously but missing now)
        const currIds = new Set(devices.map(d => d.deviceId));
        const removedDevices = prev.filter(d => d.kind === "videoinput" && !currIds.has(d.deviceId));

        prevDevicesRef.current = devices;

        if (newDevices.length > 0 && !shouldSuppressToastNotifications()) {
          try {
            if (toastCtx && typeof toastCtx.addItems === "function") {
              const items: ToastType[] = newDevices.map(d => {
                // Strip device code like "(046d:082d)" from labels if present
                const rawLabel = d.label || "Camera";
                const stripped = rawLabel.replace(/\s*\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)\s*$/, "");
                return {
                  id: Date.now() + Math.floor(Math.random() * 1000),
                  status: "info",
                  icon: "",
                  link: "",
                  title: t("toast-new-device-title"),
                  message: t("toast-new-device-body", { deviceName: stripped }),
                  linkText: "",
                };
              });
              toastCtx.addItems(items);
            }
          } catch {
            // best-effort: ignore if toast/translation not available
          }
        }

        if (removedDevices.length > 0) {
          // If the removed device was the one currently in use (or if there is
          // no active user stream and only a single device remains), attempt
          // to automatically reselect the remaining onboard camera so the
          // UI recovers without forcing the user to reselect the source.
          try {
            const videoInputs = devices.filter(d => d.kind === "videoinput");
            const prevState = stateRef.current;
            const activeDeviceId = prevState?.userStream?.getVideoTracks()?.[0]?.getSettings()?.deviceId ?? null;
            const removedIds = new Set(removedDevices.map(d => d.deviceId));

            // If the active device was removed, or there is no active stream
            // and exactly one device remains, try to restart user capture.
            if ((activeDeviceId && removedIds.has(activeDeviceId)) ||
            (!prevState?.userStream && videoInputs.length === 1)) {
              const target = videoInputs[0];
              if (target && target.deviceId) {
                const aspectRatio = preferredCameraAspectRatio();
                const userConstraints: MediaTrackConstraints = {
                  deviceId: { exact: target.deviceId },
                  ...aspectRatio && { aspectRatio: { ideal: aspectRatio } },
                };
                // Best-effort: start capture with an exact deviceId constraint.
                // We `await` the call so we can surface a helpful toast if the
                // browser rejects the request (often due to missing user gesture
                // or blocked permissions). This is still best-effort: failures
                // are not fatal to the app, but we inform the user so they can
                // re-select or allow camera access.
                try {
                  await startUserCapture(dispatch, settings, userConstraints);
                } catch {
                  try {
                    const rawLabel = target.label || "Camera";
                    const stripped = rawLabel.replace(/\s*\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)\s*$/, "");
                    if (!shouldSuppressToastNotifications() && toastCtx && typeof toastCtx.addItems === "function") {
                      toastCtx.addItems([{
                        id: Date.now() + Math.floor(Math.random() * 1000),
                        status: "warning",
                        icon: "",
                        link: "",
                        title: t("toast-device-reselect-failed-title"),
                        message: t("toast-device-reselect-failed-body", { deviceName: stripped }),
                        linkText: "",
                      }]);
                    }
                  } catch {
                    // ignore toast failures
                  }
                }
              }
            }
          } catch {
            // best-effort: ignore
          }
          try {
            if (!shouldSuppressToastNotifications() && toastCtx && typeof toastCtx.addItems === "function") {
              const items: ToastType[] = removedDevices.map(d => {
                const rawLabel = d.label || "Camera";
                const stripped = rawLabel.replace(/\s*\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)\s*$/, "");
                return {
                  id: Date.now() + Math.floor(Math.random() * 1000),
                  status: "warning",
                  icon: "",
                  link: "",
                  title: t("toast-device-removed-title"),
                  message: t("toast-device-removed-body", { deviceName: stripped }),
                  linkText: "",
                };
              });
              toastCtx.addItems(items);
            }
          } catch {
            // best-effort
          }
        }
      } catch {
        // ignore
      }
    };

    // Initial enumeration
    updateAndNotify();

    const md = (navigator.mediaDevices as unknown) as MediaDevices | undefined;
    if (md && typeof md.addEventListener === "function") {
      const handler = () => { updateAndNotify(); };
      md.addEventListener("devicechange", handler);
      return () => md.removeEventListener("devicechange", handler);
    }

    return undefined;
  }, [dispatch, settings, toastCtx, t]);

  return (
    <dispatchContext.Provider value={dispatch}>
      <stateContext.Provider value={state}>{children}</stateContext.Provider>
    </dispatchContext.Provider>
  );
};

/** Hook to get the `dispatch` function in order to change the global studio state. */
export const useDispatch = (): Dispatcher => usePresentContext(dispatchContext, "useDispatch");

/** Hook to get access to the global Studio state. */
export const useStudioState = (): StudioState => usePresentContext(stateContext, "useStudioState");
